import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, ExternalLink, Trash2, FileText, Loader2, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHead, EmptyState } from "@/components/common";
import AccountSettings from "@/components/AccountSettings";
import { RxOrderButton } from "@/components/RxOrder";
import { SimplifyButton } from "@/components/NirogyamAI";
import { FullLoader, useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, label, openSigned, useApi, BACKEND, withAct } from "@/lib/api";

const CATS = ["lab_report", "imaging", "prescription", "discharge_summary", "other"];

function Uploader({ onDone }) {
  const [file, setFile] = useState(null);
  const [cat, setCat] = useState("lab_report");
  const [notes, setNotes] = useState("");
  const [pct, setPct] = useState(null);
  const ref = useRef(null);
  const upload = async () => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File is larger than 10 MB");
    const fd = new FormData();
    fd.append("file", file); fd.append("category", cat); fd.append("notes", notes);
    setPct(0);
    try {
      await api.post("/records", fd, { onUploadProgress: (e) => setPct(Math.round((e.loaded / (e.total || 1)) * 100)) });
      toast.success("Report uploaded securely");
      setFile(null); setNotes(""); ref.current.value = ""; onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setPct(null); }
  };
  return (
    <div className="surface p-6 space-y-3" data-testid="record-uploader">
      <h3 className="font-semibold flex items-center gap-2"><Upload className="h-4 w-4 text-[#086B2D]" />Upload a report</h3>
      <Input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files[0])} data-testid="record-file-input" />
      <p className="text-xs muted">PDF, JPG, PNG or WEBP · up to 10 MB · stored privately</p>
      <Select value={cat} onValueChange={setCat}><SelectTrigger data-testid="record-category-select"><SelectValue /></SelectTrigger>
        <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{label(c)}</SelectItem>)}</SelectContent></Select>
      <Input placeholder="Optional note (e.g. Fasting sugar, March)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="record-notes-input" />
      {pct !== null && <Progress value={pct} className="h-1.5" />}
      <Button onClick={upload} disabled={!file || pct !== null} className="rounded-full" data-testid="record-upload-btn">{pct !== null && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Upload</Button>
    </div>
  );
}

export function HealthRecords() {
  const records = useApi("/records");
  const rx = useApi("/prescriptions");
  const remove = async (id) => {
    if (!window.confirm("Remove this report?")) return;
    try { const { data } = await api.delete(`/records/${id}`); toast.success(data.message || "Report removed"); records.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHead eyebrow="Free health log" title="Reports & prescriptions" sub="Sorted date-wise (newest first). Open or download anytime, or tap Simplify to let Nirogyam AI explain a report in simple words. Doctors you consult can view your records during the consultation." />
      <div className="grid lg:grid-cols-3 gap-6">
        <Uploader onDone={records.reload} />
        <div className="lg:col-span-2 space-y-6">
          <section className="surface p-6" data-testid="records-list">
            <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">My reports</h3><Link to="/patient/trends" className="text-sm text-[#2412B5] inline-flex items-center gap-1" data-testid="records-trends-link"><Activity className="h-4 w-4" />Health trends</Link></div>
            {records.loading ? <FullLoader /> : (records.data || []).length === 0 ? <EmptyState title="No reports yet" text="Upload lab reports, scans or old prescriptions." /> : records.data.map((r, i, arr) => (<div key={r.id}>
              {(i === 0 || fmtDate(arr[i - 1].created_at) !== fmtDate(r.created_at)) && <p className="eyebrow mt-4 mb-1" data-testid="records-date-group">{fmtDate(r.created_at)}</p>}
              <div key={r.id} className="flex items-center justify-between gap-3 py-3 border-b border-[#E2E8ED] last:border-0" data-testid={`record-${r.id}`}>
                <div className="min-w-0"><p className="text-sm font-medium truncate">{r.filename}</p><p className="text-xs muted capitalize">{label(r.category)} · {fmtDate(r.created_at)}{r.notes && ` · ${r.notes}`}{r.deletion_requested && " · deletion requested"}</p></div>
                <div className="flex gap-1 shrink-0 items-center">
                  <SimplifyButton rec={r} />
                  <Button size="icon" variant="ghost" onClick={() => openSigned(`/records/${r.id}/link`).catch((e) => toast.error(errMsg(e)))} data-testid={`record-view-${r.id}`} aria-label="View"><ExternalLink className="h-4 w-4" /></Button>
                  {r.source !== "partner" && <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`record-delete-${r.id}`} aria-label="Delete"><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </div>
              </div>
            </div>))}
          </section>
          <section className="surface p-6" data-testid="prescriptions-list">
            <h3 className="font-semibold mb-3">Prescriptions</h3>
            {(rx.data || []).length === 0 ? <p className="text-sm muted">No prescriptions yet.</p> : rx.data.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-3 border-b border-[#E2E8ED] last:border-0 text-sm" data-testid={`rx-${p.id}`}>
                <a href={withAct(`${BACKEND}/api/prescriptions/${p.id}/pdf`)} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#2412B5]"><FileText className="h-4 w-4 text-[#086477]" />{p.rx_number} · {p.doctor?.name} <span className="muted">{fmtDate(p.finalized_at)}</span></a>
                <RxOrderButton rx={p} /></div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}

export function PatientIdCard() {
  const { user } = useAuth();
  if (!user?.patient_code) return null;
  return <div className="mb-6 inline-flex items-center gap-3 rounded-2xl border border-[#2412B5]/20 bg-[#2412B5]/[0.04] px-4 py-2" data-testid="patient-id-card"><span className="eyebrow">Nirogyam Patient ID</span><span className="font-display font-semibold text-[#2412B5]">{user.patient_code}</span></div>;
}

const F = ({ l, children }) => <div><Label className="text-xs muted">{l}</Label><div className="mt-1.5">{children}</div></div>;

function ProfileForm() {
  const { data, loading, reload } = useApi("/patient/profile");
  const { refresh } = useAuth();
  const [f, setF] = useState(null);
  const [ai, setAi] = useState(false);
  useEffect(() => {
    if (!data) return;
    setF(data);
    const c = data.consents.find((x) => x.type === "ai_processing");
    setAi(Boolean(c?.granted));
  }, [data]);
  if (loading || !f) return <FullLoader />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const minor = f.dob && (Date.now() - new Date(f.dob)) / 31557600000 < 18;
  const save = async () => {
    const keys = ["full_name", "dob", "gender", "phone", "address", "emergency_contact_name", "emergency_contact_phone", "allergies", "medications", "medical_history", "guardian_name", "guardian_relationship", "guardian_consent"];
    try { await api.put("/patient/profile", Object.fromEntries(keys.map((k) => [k, f[k] === "" ? null : f[k] ?? null]))); toast.success("Profile saved"); reload(); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleAi = async (v) => { try { await api.post("/consents", { type: "ai_processing", granted: v }); setAi(v); toast.success("Consent preference saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      <section className="surface p-6 grid sm:grid-cols-2 gap-4" data-testid="patient-profile-form">
        <F l="Full name"><Input value={f.full_name || ""} onChange={set("full_name")} data-testid="profile-name" /></F>
        <F l="Date of birth"><Input type="date" value={f.dob || ""} onChange={set("dob")} data-testid="profile-dob" /></F>
        <F l="Gender (for clinical relevance)"><Select value={f.gender || ""} onValueChange={(v) => setF({ ...f, gender: v })}><SelectTrigger data-testid="profile-gender"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{["Female", "Male", "Other", "Prefer not to say"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></F>
        <F l="Mobile (change under Account)"><Input value={f.phone || ""} readOnly className="bg-[#F7F9FA]" data-testid="profile-phone" /></F>
        <div className="sm:col-span-2"><F l="Address (for prescriptions & diagnostics)"><Input value={f.address || ""} onChange={set("address")} data-testid="profile-address" /></F></div>
        <F l="Emergency contact name"><Input value={f.emergency_contact_name || ""} onChange={set("emergency_contact_name")} /></F>
        <F l="Emergency contact phone"><Input value={f.emergency_contact_phone || ""} onChange={set("emergency_contact_phone")} /></F>
        <div className="sm:col-span-2 border-t border-[#E2E8ED] pt-4"><p className="eyebrow">Medical information · shared only with your treating doctor</p></div>
        <F l="Allergies"><Textarea value={f.allergies || ""} onChange={set("allergies")} data-testid="profile-allergies" /></F>
        <F l="Current medications"><Textarea value={f.medications || ""} onChange={set("medications")} data-testid="profile-medications" /></F>
        <div className="sm:col-span-2"><F l="Medical history"><Textarea value={f.medical_history || ""} onChange={set("medical_history")} data-testid="profile-history" /></F></div>
        {minor && (
          <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4 rounded-xl bg-[#FF9700]/10 p-4" data-testid="guardian-section">
            <p className="sm:col-span-2 text-sm text-[#9A5B00]">Patient is under 18 – a parent/guardian must consent and be present during consultations.</p>
            <F l="Guardian name"><Input value={f.guardian_name || ""} onChange={set("guardian_name")} data-testid="guardian-name" /></F>
            <F l="Relationship"><Input value={f.guardian_relationship || ""} onChange={set("guardian_relationship")} /></F>
            <label className="sm:col-span-2 flex items-center gap-3 text-sm"><Switch checked={!!f.guardian_consent} onCheckedChange={(v) => setF({ ...f, guardian_consent: v })} data-testid="guardian-consent" />I am the parent/guardian and consent to teleconsultation.</label>
          </div>
        )}
        <div className="sm:col-span-2"><Button className="rounded-full" onClick={save} data-testid="profile-save-btn">Save profile</Button></div>
      </section>
      <section className="surface p-6" data-testid="consent-preferences">
        <h3 className="font-semibold">Consent preferences</h3>
        <label className="mt-4 flex items-start gap-3 text-sm"><Switch checked={ai} onCheckedChange={toggleAi} data-testid="consent-ai-toggle" /><span>Allow my de-identified presenting complaint to be used by AI tools that help my doctor draft notes. Doctors always review drafts. You can withdraw anytime.</span></label>
        <p className="mt-3 text-xs muted">Terms, privacy and telemedicine consent were given at registration.</p>
      </section>
    </div>
  );
}

export function PatientProfile() {
  return (
    <div>
      <PatientIdCard />
      <PageHead eyebrow="Profile" title="Your profile" sub="Fill in only what's useful for your care. You can book without completing everything." />
      <Tabs defaultValue="profile"><TabsList><TabsTrigger value="profile" data-testid="profile-tab">Profile</TabsTrigger><TabsTrigger value="account" data-testid="account-tab">Account & security</TabsTrigger></TabsList>
        <TabsContent value="profile" className="mt-6"><ProfileForm /></TabsContent>
        <TabsContent value="account" className="mt-6"><AccountSettings /></TabsContent>
      </Tabs>
    </div>
  );
}
